import { createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

import { client } from 'src/graphql/client';
import { ESCALATE_STOPS_TO_PROVIDER } from 'src/graphql/requests/escalateStopsToProvider';
import {
  GEscalateStopsToProviderMutation,
  GThirdPartyProvider,
} from 'src/graphql/types/generated';
import { logWarning } from 'src/services/datadog';
import type { AppDispatch } from 'src/state/store';
import { firstUserErrorMessage } from 'src/utils';
import { NotificationType, showNotification } from '../notification';
import { refetchRouteList } from '../delivery/refetchRouteList';
import type { ProviderRouteResult } from '../delivery/markRouteForProvider';
import { markDisplayedStopsRecovered } from './createRedeliveryStops';
import { IssuesState } from './state';

type EscalateStopsToProviderArgs = {
  warehouseId: string;
  nightEnd: string;
  stopIds: string[];
  provider: GThirdPartyProvider;
  // ISO8601 in UTC; omitted means ASAP, decided by LDS at push time.
  pickupAt?: string;
};

export type EscalatedRoute = NonNullable<
  NonNullable<
    GEscalateStopsToProviderMutation['escalateStopsToProvider']
  >['route']
>;

export type EscalateStopsResult = ProviderRouteResult & {
  route?: EscalatedRoute;
};

export const escalateStopsToProvider = createAsyncThunk(
  'issues/escalateStopsToProvider',
  async (
    {
      warehouseId,
      nightEnd,
      stopIds,
      provider,
      pickupAt,
    }: EscalateStopsToProviderArgs,
    thunkAPI,
  ): Promise<EscalateStopsResult> => {
    // Cached, so the Issues table keeps its rows and the GM's selection.
    const refetch = (): void =>
      refetchRouteList(thunkAPI.dispatch as AppDispatch, {
        warehouseId,
        nightEnd,
        cached: true,
      });

    const { data, error } = await client
      .mutation<GEscalateStopsToProviderMutation>(ESCALATE_STOPS_TO_PROVIDER, {
        input: {
          warehouseId,
          nightEnd,
          stopIds,
          provider,
          ...(pickupAt ? { pickupAt } : {}),
        },
      })
      .toPromise();

    const payload = data?.escalateStopsToProvider;
    const userErrors = payload?.userErrors ?? [];
    const logContext = {
      warehouseId,
      nightEnd,
      stopCount: stopIds.length,
      provider,
    };

    if (userErrors.length > 0) {
      logWarning('third_party.escalate_stops_failed', {
        ...logContext,
        userError: userErrors[0]?.__typename,
        transportError: false,
      });
      // A refusal creates nothing, but another GM may have recovered one of
      // the stops first: the route list shows it, while the Issues rows keep
      // their status until their own query reloads. The modal stays open with
      // LDS's message, which names the stop.
      refetch();
      return { ok: false, message: firstUserErrorMessage(userErrors) };
    }

    // A payload with neither a route nor a refusal is as unknown as a lost
    // response: the route may exist, so the GM checks before retrying.
    if (error || !payload?.route) {
      logWarning('third_party.escalate_stops_failed', {
        ...logContext,
        transportError: Boolean(error),
      });
      thunkAPI.dispatch(showNotification({ type: NotificationType.Error }));
      refetch();
      return { ok: false };
    }

    // No toast: the courier dialog takes over on the new route.
    refetch();
    return { ok: true, route: payload.route };
  },
);

export const escalateStopsToProviderFulfillment = (
  state: IssuesState,
  action: PayloadAction<EscalateStopsResult>,
): void =>
  markDisplayedStopsRecovered(
    state,
    action.payload.route?.redeliveryStops ?? [],
  );
